import { useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import { useRouteParams } from '@/contexts/RouteContext';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useOne } from '@/hooks/resourceQueries';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { ParkingSessionForm } from './parking-session-form';
import {
	buildChangePlateNumberInput,
	mapParkingSessionToFormData,
	type ParkingSessionFormSubmitData
} from './parking-session-input';
import {
	PARKING_SESSION_DETAIL_QUERY,
	CHANGE_PARKING_SESSION_PLATE_NUMBER_MUTATION
} from './parking-sessions.gql';

interface ParkingSessionRecord {
	parkingSessionId: string;
	vehicleId?: string | null;
	vehicle?: {
		vehicleId: string;
		plateNumber?: string | null;
	} | null;
}

const isValidUlid = (id: string): boolean => {
	if (!id?.length || id.length !== 26) return false;
	const ulidRegex = /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/;
	return ulidRegex.test(id.toUpperCase());
};

export const ParkingSessionsEdit = () => {
	const { t } = useTranslation();
	const go = useGo();
	const { id } = useRouteParams();
	const { setPageHeader, resetPageHeader } = usePageHeader();

	const {
		data: parkingSessionResponse,
		isLoading,
		isError,
		error: fetchError
	} = useOne<ParkingSessionRecord>({
		resource: 'parkingSessions',
		id: id ?? '',
		meta: {
			gqlQuery: PARKING_SESSION_DETAIL_QUERY,
			dataPath: 'parkingSession',
			variables: { parkingSessionId: id }
		},
		queryOptions: {
			enabled: Boolean(id) && isValidUlid(id ?? '')
		}
	});

	const parkingSession = parkingSessionResponse?.data;

	// Use delayed loading to prevent spinner flash for quick loads
	const { shouldShowLoading } = useDelayedLoading(isLoading, {
		delay: 300,
		showInitialLoading: false
	});

	const [changePlateNumber, { loading: isSubmitting }] = useMutation(
		CHANGE_PARKING_SESSION_PLATE_NUMBER_MUTATION
	);

	const initialFormData = useMemo(
		() =>
			parkingSession
				? {
						...mapParkingSessionToFormData(
							parkingSession.vehicle?.plateNumber,
							parkingSession.vehicle?.vehicleId
						),
						parkingSessionId: parkingSession.parkingSessionId
				  }
				: undefined,
		[parkingSession]
	);

	useEffect(() => {
		setPageHeader({
			title: t('parkingSessions:edit.title'),
			description: t('parkingSessions:edit.description'),
			showBackButton: true,
			onBackClick: () => go({ to: '/parking-sessions' })
		});

		return () => {
			resetPageHeader();
		};
	}, [setPageHeader, resetPageHeader, go, t]);

	// Handle invalid ULID case
	if (id && !isValidUlid(id)) {
		return (
			<FormPageLayout>
				<Card className="p-6">
					<div className="space-y-4 text-center">
						<div className="text-lg font-medium text-red-500">
							{t('common:errors.invalidId')}
						</div>
						<p className="text-muted-foreground">
							{t('parkingSessions:errors.invalidSessionId')}:{' '}
							<code className="px-2 py-1 bg-muted rounded">
								{id}
							</code>
						</p>
						<Button
							variant="outline"
							onClick={() => go({ to: '/parking-sessions' })}
						>
							{t('common:buttons.back')}
						</Button>
					</div>
				</Card>
			</FormPageLayout>
		);
	}

	if (!id) {
		return (
			<FormPageLayout>
				<Alert variant="destructive">
					<AlertTitle>
						{t('parkingSessions:errors.notFound')}
					</AlertTitle>
					<AlertDescription>
						{t('parkingSessions:errors.noIdProvided')}
					</AlertDescription>
				</Alert>
			</FormPageLayout>
		);
	}

	if (shouldShowLoading) {
		return (
			<FormPageLayout>
				<Card className="p-6">
					<div className="flex items-center justify-center h-32">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
					</div>
				</Card>
			</FormPageLayout>
		);
	}

	// Wait for data to be available before showing error or form
	// This prevents showing error state during initial load when shouldShowLoading is false
	if (isLoading && !parkingSession) {
		// Still loading, but not showing spinner - render nothing to prevent jitter
		return (
			<FormPageLayout>
				<Card className="p-6">
					<div className="h-32" />
				</Card>
			</FormPageLayout>
		);
	}

	if (isError || !parkingSession) {
		return (
			<FormPageLayout>
				<Alert variant="destructive">
					<AlertTitle>
						{t('parkingSessions:errors.loadFailed')}
					</AlertTitle>
					<AlertDescription>
						{fetchError?.message ??
							t('parkingSessions:errors.unknownError')}
					</AlertDescription>
				</Alert>
				<div className="mt-4">
					<Button
						variant="outline"
						onClick={() => go({ to: '/parking-sessions' })}
					>
						{t('common:buttons.back')}
					</Button>
				</div>
			</FormPageLayout>
		);
	}

	const handleSubmit = async (formData: ParkingSessionFormSubmitData) => {
		if (!parkingSession?.parkingSessionId) {
			showMutationErrorToast('parkingSessions', 'update', {
				message: t('parkingSessions:errors.missingId')
			});
			return;
		}

		const payload = buildChangePlateNumberInput(
			parkingSession.parkingSessionId,
			formData.plateNumber
		);

		try {
			const { data } = await changePlateNumber(
				getMutationOptions('parkingSessions', { input: payload })
			);

			const message = extractCheckResultError(
				data?.changeParkingSessionPlateNumber
			);

			if (message) {
				showMutationErrorToast('parkingSessions', 'update', { message });
				return;
			}

			showMutationSuccessToast('parkingSessions', 'update');
			go({
				to: '/parking-sessions',
				state: { selectParkingSessionId: parkingSession.parkingSessionId }
			});
		} catch (error) {
			showMutationErrorToast('parkingSessions', 'update', { error });
		}
	};

	return (
		<FormPageLayout>
			<Card>
				<ParkingSessionForm
					mode="edit"
					initialData={initialFormData}
					onSubmit={handleSubmit}
					isSubmitting={isSubmitting}
					headerTitle={t('parkingSessions:edit.title')}
				/>
			</Card>
		</FormPageLayout>
	);
};
